// @ts-nocheck
/**
 * "Tie-Out" tab body for the FinalMappingCard.
 *
 * Renders the provenance ledger: for every standardized line, the source cell
 * its exported formula points at, the claimed value vs the value actually
 * sitting in that cell, and a tie/flag verdict — so the user can see the
 * numbers tie to source without checking by hand, and any that don't are
 * flagged. Each row expands to its audit trail (classification reasoning, any
 * reconciliation correction, and the per-period breakdown).
 *
 * Data is fetched once by FinalMappingCard and passed in (shared with the
 * header TrustBadge) so opening the tab never triggers a second request.
 */
import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  RefreshCw,
} from 'lucide-react';

import { useTaxonomy } from './taxonomyContext';
import TieBadge from './TieBadge';
import './TieOutTab.css';

const fmtCell = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return n < 0 ? `(${formatted})` : formatted;
};

// Flagged first, then unverifiable, then unlinked, then ties; row order within.
const STATUS_RANK = { flag: 0, source_unavailable: 1, not_referenced: 2, tie: 3 };

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'flagged', label: 'Flagged' },
  { key: 'tied', label: 'Tied' },
  { key: 'not_linked', label: 'Not linked' },
];

const matchesFilter = (line, filter) => {
  if (filter === 'flagged') return line.tie_status === 'flag';
  if (filter === 'tied') return line.tie_status === 'tie';
  if (filter === 'not_linked')
    return line.tie_status === 'not_referenced' || line.tie_status === 'source_unavailable';
  return true;
};

const TieOutTab = ({ data, loading, error, filter, onFilterChange, onRetry, hasEdits }) => {
  const { badgeStyle } = useTaxonomy();
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (rowIndex) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });

  const lines = data?.lines || [];
  const counts = data?.counts || {};
  const referencingAvailable = data?.referencing_available !== false;

  const visible = useMemo(() => {
    const filtered = lines.filter((l) => matchesFilter(l, filter));
    return [...filtered].sort((a, b) => {
      const ra = STATUS_RANK[a.tie_status] ?? 9;
      const rb = STATUS_RANK[b.tie_status] ?? 9;
      if (ra !== rb) return ra - rb;
      return (a.row_index ?? 0) - (b.row_index ?? 0);
    });
  }, [lines, filter]);

  if (loading) {
    return (
      <div className="rt-state">
        <Loader2 size={16} className="rt-spin" />
        <span>Checking every figure against source…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rt-state rt-state-error">
        <AlertTriangle size={16} />
        <span>{error || 'Could not load the tie-out ledger.'}</span>
        {onRetry && (
          <button type="button" className="tieout-btn" onClick={onRetry}>
            <RefreshCw size={13} /> Try again
          </button>
        )}
      </div>
    );
  }

  if (!data || lines.length === 0) {
    return (
      <div className="it-empty">
        <span>Tie-out is not available for this mapping yet.</span>
      </div>
    );
  }

  return (
    <div className="tieout-wrap">
      <div className="tieout-summary">
        <div className="tieout-counts">
          <span className="tieout-count tieout-count-tie">{counts.tie || 0} tie</span>
          <span className="tieout-count tieout-count-flag">{counts.flag || 0} flag</span>
          {(counts.source_unavailable || 0) > 0 && (
            <span className="tieout-count tieout-count-warn">
              {counts.source_unavailable} no source
            </span>
          )}
          {(counts.not_referenced || 0) > 0 && (
            <span className="tieout-count tieout-count-muted">
              {counts.not_referenced} not linked
            </span>
          )}
        </div>
        <div className="tieout-filters" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`tieout-filter ${filter === f.key ? 'is-active' : ''}`}
              onClick={() => onFilterChange?.(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!referencingAvailable && (
        <div className="tieout-note">
          This mapping is not cell-linked, so values are not tied to source cells.
          Turn on cell references in the export panel to link them.
        </div>
      )}
      {hasEdits && (
        <div className="tieout-note tieout-note-muted">
          Showing the saved mapping. Unsaved edits are not reflected here yet.
        </div>
      )}

      <div className="fmc-table-wrap">
        <table className="fmc-table tieout-table">
          <thead>
            <tr>
              <th className="fmc-col-account">Account</th>
              <th className="fmc-col-category">Category</th>
              <th>Source cell</th>
              <th className="fmc-num">Claimed</th>
              <th className="fmc-num">Source</th>
              <th className="fmc-num">Δ</th>
              <th>Tie-out</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((line) => {
              const open = expanded.has(line.row_index);
              const hasDelta = typeof line.delta === 'number' && Math.abs(line.delta) > 0.005;
              return (
                <React.Fragment key={line.row_index}>
                  <tr
                    className={`fmc-row tieout-row ${line.corrected ? 'fmc-corrected' : ''} ${line.sign_flipped ? 'fmc-sign-flipped' : ''} ${open ? 'is-open' : ''}`}
                  >
                    <td className="fmc-account" title={line.account_name || ''}>
                      <button
                        type="button"
                        className="tieout-expand"
                        onClick={() => toggle(line.row_index)}
                        aria-expanded={open}
                      >
                        <ChevronDown size={14} className="tieout-chevron" />
                        <span className="fmc-account-text">
                          {line.account_name || <span className="fmc-muted">—</span>}
                        </span>
                        {line.sign_flipped && (
                          <span
                            className="fmc-sign-flipped-tag"
                            title="Source value was negative; mapped as a positive magnitude (sign swapped)."
                          >
                            sign flipped
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="fmc-category">
                      <span className="tieout-cat" style={badgeStyle(line.category)}>
                        {line.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="tieout-cell">
                      {line.source_cell || <span className="fmc-muted">—</span>}
                      {line.is_formula && (
                        <span className="tieout-fx" title={line.source_formula || 'Source cell is a formula'}>
                          ƒx
                        </span>
                      )}
                    </td>
                    <td className="fmc-num">{fmtCell(line.claimed_value)}</td>
                    <td className="fmc-num">{fmtCell(line.resolved_value)}</td>
                    <td className={`fmc-num ${hasDelta ? 'fmc-neg' : ''}`}>
                      {hasDelta ? fmtCell(line.delta) : '—'}
                    </td>
                    <td>
                      <TieBadge status={line.tie_status} />
                    </td>
                  </tr>

                  {open && (
                    <tr className="tieout-detail-row">
                      <td colSpan={7}>
                        <div className="tieout-detail">
                          {line.classification_reasoning && (
                            <p className="tieout-line">
                              <span className="tieout-line-tag">Why this category</span>
                              {line.classification_reasoning}
                              {typeof line.classification_confidence === 'number' && (
                                <span className="tieout-conf">
                                  {Math.round(line.classification_confidence * 100)}% confident
                                </span>
                              )}
                            </p>
                          )}
                          {line.correction && (
                            <p className="tieout-line tieout-correction">
                              <span className="tieout-line-tag">Reclassified</span>
                              {(line.correction.old_category ?? '?')} → {(line.correction.new_category ?? '?')}
                              {line.correction.reasoning ? ` · ${line.correction.reasoning}` : ''}
                            </p>
                          )}
                          {line.source_formula && (
                            <p className="tieout-line">
                              <span className="tieout-line-tag">Source formula</span>
                              <code className="tieout-formula">{line.source_formula}</code>
                            </p>
                          )}
                          {line.per_period && line.per_period.length > 0 && (
                            <table className="tieout-period-table">
                              <thead>
                                <tr>
                                  <th>Period</th>
                                  <th>Cell</th>
                                  <th className="fmc-num">Claimed</th>
                                  <th className="fmc-num">Source</th>
                                  <th className="fmc-num">Δ</th>
                                  <th>Tie-out</th>
                                </tr>
                              </thead>
                              <tbody>
                                {line.per_period.map((pp) => {
                                  const d = typeof pp.delta === 'number' && Math.abs(pp.delta) > 0.005;
                                  return (
                                    <tr key={pp.period_index}>
                                      <td>{pp.period_display_name}</td>
                                      <td className="tieout-cell">
                                        {pp.source_cell || '—'}
                                      </td>
                                      <td className="fmc-num">{fmtCell(pp.claimed_value)}</td>
                                      <td className="fmc-num">{fmtCell(pp.resolved_value)}</td>
                                      <td className={`fmc-num ${d ? 'fmc-neg' : ''}`}>
                                        {d ? fmtCell(pp.delta) : '—'}
                                      </td>
                                      <td>
                                        <TieBadge status={pp.status} compact />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                          {line.source_row_account_name &&
                            line.account_name &&
                            line.source_row_account_name.trim().toLowerCase() !==
                              line.account_name.trim().toLowerCase() && (
                              <p className="tieout-line tieout-warn-line">
                                <span className="tieout-line-tag">Heads up</span>
                                The source row reads “{line.source_row_account_name}”, not “
                                {line.account_name}”.
                              </p>
                            )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.notes && data.notes.length > 0 && (
        <div className="tieout-footnotes">
          {data.notes.map((n, i) => (
            <div key={i} className="tieout-footnote">
              {n}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TieOutTab;
